import { differenceInMinutes } from 'date-fns';
import { useTranslation } from '../../../i18n/i18n';
import { cn } from '../../../utils/cn';
import ModalShell from '../../../components/ui/ModalShell';

import { formatDateTime, formatDetailDateTime, formatHoursMinutesLabel, toDate } from '../../../utils/timeUtils';
import { useRecipeDisplay } from '../../../context/RecipeDisplayContext';

export default function ProductionRunDetailsModal({
  productionRun,
  productionEvents = [],
  currentUser,
  onClose,
  onCorrectionClick,
  onInstructionClick,
}) {
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const { activeUserName } = useRecipeDisplay();
  if (!productionRun) return null;

  const now = new Date();
  const startDate = toDate(productionRun.start_date);
  const endDate = toDate(productionRun.end_date);

  if (!startDate || !endDate) return null;

  const timeLeftMinutes = now < endDate ? differenceInMinutes(endDate, now) : 0;

  // Source corrections and instructions from productionEvents filtered by production_id.
  const runEvents = productionEvents.filter((e) => e?.production_id === productionRun.id);

  const allInstructions = runEvents.filter((e) => e?.type === 'production_instruction');
  const latestInstruction =
    allInstructions.sort(
      (a, b) => (toDate(b?.created_at)?.getTime() || 0) - (toDate(a?.created_at)?.getTime() || 0),
    )[0] || null;

  const lastMaterialCorrection =
    runEvents
      .filter((e) => e?.type === 'material_correction' && e?.executed !== true)
      .sort((a, b) => (toDate(b?.created_at)?.getTime() || 0) - (toDate(a?.created_at)?.getTime() || 0))[0] || null;

  const lastTimeCorrection =
    runEvents
      .filter((e) => e?.type === 'time_correction')
      .sort((a, b) => (toDate(b?.created_at)?.getTime() || 0) - (toDate(a?.created_at)?.getTime() || 0))[0] || null;

  const latestCorrections = [lastMaterialCorrection, lastTimeCorrection].filter(Boolean);

  return (
    <ModalShell title={t('production_details_title')} onClose={onClose} maxWidth='40.625rem' bodyClassName=''>
      <div className='py-6 mx-6 flex border-b dark:border-b-[#3C3E53] gap-4'>
        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_details_start_time')}
          </span>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {formatDetailDateTime(startDate, currentUser?.info?.languageCode)}
          </div>
        </div>
        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_details_time_left')}
          </span>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {formatHoursMinutesLabel(timeLeftMinutes)}
          </div>
        </div>
      </div>
      <div className='p-6'>
        <div className='text-lg font-semibold text-[#16192C] dark:text-[#FAFAFA]'>
          {t('production_details_activity')}
        </div>
        <div className='mt-4 space-y-3'>
          {latestCorrections.length === 0 && !latestInstruction && (
            <div className='rounded-lg p-3 bg-[#F3F7FF] dark:bg-[#2E3440] text-[#8A8C95] dark:text-[#CAD5E2] text-sm'>
              {t('production_details_no_activity')}
            </div>
          )}
          {latestCorrections.length > 0 &&
            latestCorrections.map((correction) => {
              const isConfirmed =
                Boolean(activeUserName) &&
                Array.isArray(correction?.confirmed_by) &&
                correction.confirmed_by.includes(activeUserName);
              const createdAt = toDate(correction?.created_at);
              return (
                <button
                  key={correction.id || correction.type}
                  type='button'
                  onClick={() => onCorrectionClick?.(correction)}
                  className='w-full text-left rounded-lg p-3 bg-[#F3F7FF] border border-[#DDE6F9] dark:border-none dark:bg-[#2E3440]'
                >
                  <div className='flex items-center justify-between gap-3'>
                    <div className='text-base leading-[140%] font-semibold text-[#16192C] dark:text-[#FAFAFA] truncate'>
                      {correction.type === 'time_correction'
                        ? t('production_corrections_time_correction')
                        : t('production_corrections_material_correction')}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 text-sm leading-4 font-medium p-2 rounded',
                        isConfirmed
                          ? 'text-[#EAFFE9] dark:text-[#89FD87] bg-[#009D0D] dark:bg-[#004B06]'
                          : 'text-[#FFE9EA] dark:text-[#FF8989] bg-[#FF0000] dark:bg-[#4B0000]',
                      )}
                    >
                      {isConfirmed
                        ? t('production_details_status_confirmed')
                        : t('production_details_status_not_confirmed')}
                    </span>
                  </div>
                  <div className='mt-3 flex gap-4'>
                    <div className='flex-1'>
                      <span className='text-sm leading-normal font-medium text-[#737580] dark:text-[#CAD5E2]'>
                        {t('production_corrections_created')}
                      </span>
                      <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
                        {createdAt
                          ? formatDateTime(createdAt, currentUser?.info?.languageCode)
                          : t('common_not_available')}
                      </div>
                    </div>
                    <div className='flex-1'>
                      <span className='text-sm leading-normal font-medium text-[#737580] dark:text-[#CAD5E2]'>
                        {t('production_details_reason')}
                      </span>
                      <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
                        {correction.reasoning || t('common_not_available')}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
        </div>
        <div className='mt-3'>
          {latestInstruction && (
            <button
              type='button'
              onClick={() => onInstructionClick?.(latestInstruction)}
              className='w-full text-left rounded-lg p-3 bg-[#F3F7FF] border border-[#DDE6F9] dark:border-none dark:bg-[#2E3440]'
            >
              <div className='flex items-center justify-between gap-1'>
                <div className='text-base leading-[140%] font-semibold text-[#16192C] dark:text-[#FAFAFA] truncate'>
                  {t('production_details_instruction_latest')}
                </div>
                <span
                  className={cn(
                    'shrink-0 text-sm leading-4 font-medium p-2 rounded',
                    Boolean(activeUserName) &&
                      Array.isArray(latestInstruction?.confirmed_by) &&
                      latestInstruction.confirmed_by.includes(activeUserName)
                      ? 'text-[#EAFFE9] dark:text-[#89FD87] bg-[#009D0D] dark:bg-[#004B06]'
                      : 'text-[#FFE9EA] dark:text-[#FF8989] bg-[#FF0000] dark:bg-[#4B0000]',
                  )}
                >
                  {Boolean(activeUserName) &&
                  Array.isArray(latestInstruction?.confirmed_by) &&
                  latestInstruction.confirmed_by.includes(activeUserName)
                    ? t('production_details_status_confirmed')
                    : t('production_details_status_not_confirmed')}
                </span>
              </div>
              <div className='mt-3 flex gap-4'>
                <div className='flex-1'>
                  <span className='text-sm leading-normal font-medium text-[#737580] dark:text-[#CAD5E2]'>
                    {t('production_instruction_created')}
                  </span>
                  <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
                    {toDate(latestInstruction.created_at)
                      ? formatDateTime(toDate(latestInstruction.created_at), currentUser?.info?.languageCode)
                      : t('common_not_available')}
                  </div>
                </div>
                <div className='flex-1'>
                  <span className='text-sm leading-normal font-medium text-[#737580] dark:text-[#CAD5E2]'>
                    {t('production_details_reason')}
                  </span>
                  <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
                    {latestInstruction.instruction || t('common_not_available')}
                  </div>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
